import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  effect,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { BehaviorSubject, filter, map, Observable, Subscription } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { GlobalBookingTripDates, PriceCalendarCTAParams, PriceCalendarParams } from '@fcom/common';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { ButtonMode, ButtonSize, ButtonTheme, DateRange } from '@fcom/ui-components';
import { GlobalBookingTravelClass, TripType } from '@fcom/core';
import { PaxAmount } from '@fcom/dapi';
import { LocationPair } from '@fcom/common/store/store.interface';
import {
  PriceCalendarComponent,
  PriceCalendarType,
} from '@fcom/common/components/price-calendar/price-calendar.component';

import { BookingWidgetService } from '../../services/booking-widget.service';
import { SelectorType } from '../../interfaces/booking-widget.interface';
@Component({
  selector: 'fin-travel-dates-selector',
  templateUrl: 'travel-dates-selector.component.html',
  styleUrls: ['./travel-dates-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TravelDatesSelectorComponent implements OnInit, OnDestroy {
  private bookingWidgetService = inject(BookingWidgetService);

  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TripType = TripType;
  readonly PriceCalendarType = PriceCalendarType;

  readonly priceCalendarType = input<PriceCalendarType>(PriceCalendarType.PRICES);

  readonly tripType = input<TripType>(undefined);

  readonly travelClass = input<GlobalBookingTravelClass>(undefined);

  readonly paxAmount = input<PaxAmount>(undefined);

  readonly locations = input<LocationPair>(undefined);

  readonly disabled = input(false);

  readonly previousFlightDepartureDate = input<LocalDate>(null);

  readonly travelDates = input<GlobalBookingTripDates>(undefined);

  readonly isOneway = input(false);

  readonly flexibleDates = input(false);

  readonly showFlexibleDatesSelection = input(true);

  readonly showTripPriceFrom = input(true);

  readonly showPerAdultPrice = input(true);

  readonly showSingular = input<keyof GlobalBookingTripDates>(undefined);

  readonly showSeparator = input(true);

  readonly showAddReturn = input(false);

  readonly showSubtitle = input(false);

  readonly enableNewSearchAutomatically = input(false);

  readonly isSearchEnabled = input(false);

  readonly setTravelDates = output<GlobalBookingTripDates>();

  readonly setFlexibleDates = output<boolean>();

  readonly addReturn = output<void>();

  readonly startSearch = output<void>();

  readonly cancelSelection = output<void>();

  private readonly priceCalendar = viewChild(PriceCalendarComponent);

  subscription = new Subscription();

  modalOpen$ = new BehaviorSubject<boolean>(false);

  private readonly previousFlightDepartureDate$ = toObservable(this.previousFlightDepartureDate);
  readonly disabledDateRanges$: Observable<DateRange[]> = this.previousFlightDepartureDate$.pipe(
    map((departureDate: LocalDate) => (departureDate ? [[LocalDate.now(), departureDate.minusDays(1)]] : []))
  );

  priceCalendarParams$: BehaviorSubject<PriceCalendarParams> = new BehaviorSubject(null);
  showEnhancedCalendar$: Observable<boolean>;

  constructor() {
    effect(() => {
      if (this.bookingWidgetService.modifyType() === SelectorType.TRAVEL_DATES) {
        this.openModal();
      }
    });
  }

  ngOnInit(): void {
    this.subscription.add(
      this.modalOpen$.pipe(filter(Boolean)).subscribe(() => {
        const { origin, destination } = this.locations() ?? ({} as LocationPair);
        this.priceCalendarParams$.next({
          origin: origin?.locationCode,
          destination: destination?.locationCode,
          tripType: this.isOneway() ? TripType.ONEWAY : this.tripType(),
          travelClass: this.travelClass(),
          paxAmount: this.paxAmount(),
          initialTravelDates: this.travelDates(),
        });
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  openModal(): void {
    this.modalOpen$.next(true);
  }

  closePriceCalendar(): void {
    if (this.enableNewSearchAutomatically()) {
      this.cancelSelection.emit();
      this.priceCalendar()?.startSearchWithSelectedDates(true);
    }
    this.modalOpen$.next(false);
    this.bookingWidgetService.resetModifyType();
  }

  priceCalendarCTAClicked(event: PriceCalendarCTAParams): void {
    this.setFlexibleDates.emit(event.flexibleDates);
    this.closePriceCalendar();
  }

  addReturnClicked(): void {
    this.addReturn.emit();
  }

  travelDatesChanged(travelDates: GlobalBookingTripDates): void {
    this.setTravelDates.emit(travelDates);
  }
}
